"use client";

import { MousePointerSquareDashed } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { BlockList, sameBlockList } from "../../../lib/types";
import { useDropTarget } from "../../../hooks/useBlockDnd";

// Drop feedback is editor chrome: platform primary, never the lomda's own
// primary, which can match the page and vanish.

/** The insertion line, centred in the `gap` (px) above the block at `index`. */
export function DropLine({
    list,
    index,
    gap,
}: {
    list: BlockList;
    index: number;
    gap: number;
}) {
    const target = useDropTarget();
    if (!target || target.index !== index || !sameBlockList(target.list, list))
        return null;
    return (
        <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 z-40 h-[3px] rounded-full bg-[hsl(var(--platform-primary))] shadow-[0_0_0_2px_hsl(var(--platform-primary)/0.25)]"
            style={{ top: -gap / 2 - 1.5 }}
        />
    );
}

/** Tints the column / tab / step a drop will land in. Its parent must be
 *  `relative`. */
export function SlotHighlight({ list }: { list: BlockList }) {
    const target = useDropTarget();
    if (!target || !sameBlockList(target.list, list)) return null;
    return (
        <span
            aria-hidden
            className="pointer-events-none absolute -inset-1 rounded-lg bg-[hsl(var(--platform-primary)/0.06)] ring-2 ring-[hsl(var(--platform-primary)/0.5)]"
        />
    );
}

/** The "drop here" box at the end of a list. It lights up for a drop at the
 *  end, so it's the indicator for an empty slot or page too. */
export function DropHereBox({
    list,
    count,
    className,
}: {
    list: BlockList;
    count: number;
    className?: string;
}) {
    const target = useDropTarget();
    const active =
        !!target && sameBlockList(target.list, list) && target.index >= count;
    return (
        <div
            className={cn(
                "flex items-center justify-center gap-2 border-2 border-dashed lomda-v3-chrome text-sm font-medium transition",
                active
                    ? "border-[hsl(var(--platform-primary))] bg-[hsl(var(--platform-primary)/0.05)] text-[hsl(var(--platform-primary))]"
                    : "border-border text-muted-foreground",
                className
            )}
        >
            <MousePointerSquareDashed className="size-4" />
            {t("lomda-v3-editor.canvas.drop-here")}
        </div>
    );
}
